import { useState, useEffect, useRef } from 'react'
import { invoicesService } from '@/services/invoices-service'
import { PortalDropdown } from '@/components/ui/portal-dropdown'

interface RecordPaymentModalProps {
  invoiceId: string
  invoiceNumber: string | null
  amountDue: number
  open: boolean
  onClose: () => void
  onRecorded: () => void
}

type PaymentMethod = 'credit_card' | 'ach_bank_transfer' | 'check' | 'cash' | 'wire_transfer' | 'other'

/* ── helpers ── */
function formatWithCommas(val: string): string {
  // Strip everything except digits and one decimal point
  const clean = val.replace(/[^0-9.]/g, '')
  const parts = clean.split('.')
  const intPart = parts[0] || ''
  const decPart = parts.length > 1 ? '.' + parts[1].slice(0, 2) : ''
  // Add commas to integer portion
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return withCommas + decPart
}

function stripCommas(val: string): string {
  return val.replace(/,/g, '')
}

/* ── Inline Calendar (matches bid-totals dp-* styles) ── */
function InlineCalendar({ value, onChange }: { value: Date; onChange: (d: Date) => void }) {
  const [currentMonth, setCurrentMonth] = useState(new Date(value.getFullYear(), value.getMonth()))

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate()
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay()
  const days = Array(firstDayOfMonth).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1))

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))
  }
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
  }
  const handleSelectDate = (day: number) => {
    onChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))
  }
  const isSelected = (day: number) =>
    day === value.getDate() &&
    currentMonth.getMonth() === value.getMonth() &&
    currentMonth.getFullYear() === value.getFullYear()

  const today = new Date()
  const isToday = (day: number) =>
    day === today.getDate() &&
    currentMonth.getMonth() === today.getMonth() &&
    currentMonth.getFullYear() === today.getFullYear()

  const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  return (
    <div className="dp">
      <div className="dp-header">
        <button type="button" onClick={handlePrevMonth} className="dp-nav">
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span className="dp-month">{monthYear}</span>
        <button type="button" onClick={handleNextMonth} className="dp-nav">
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
      <div className="dp-days-header">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="dp-grid">
        {days.map((day, index) => (
          <button
            type="button"
            key={index}
            onClick={() => day && handleSelectDate(day)}
            disabled={!day}
            className={day ? `dp-cell${isSelected(day) ? ' selected' : ''}${isToday(day) && !isSelected(day) ? ' today' : ''}` : ''}
            style={!day ? { visibility: 'hidden' } : {}}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  )
}

/* ── Date Received Picker (text field + dropdown calendar) ── */
function DateReceivedPicker({ value, onChange }: { value: Date; onChange: (d: Date) => void }) {
  const [calendarOpen, setCalendarOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const formatted = value.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const parsed = new Date(raw)
    if (!isNaN(parsed.getTime())) {
      onChange(parsed)
    }
  }

  const handleCalendarSelect = (d: Date) => {
    onChange(d)
    setCalendarOpen(false)
  }

  return (
    <div className="inv-date-picker-wrapper" ref={wrapperRef}>
      <div className="inv-date-picker-field">
        <input
          type="text"
          value={formatted}
          onChange={handleInputChange}
          className="inv-modal-input inv-date-picker-input"
          placeholder="MM/DD/YYYY"
        />
        <button
          type="button"
          className="inv-date-picker-toggle"
          onClick={() => setCalendarOpen(!calendarOpen)}
          aria-label="Toggle calendar"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>
      </div>
      {/* Portaled so the calendar isn't clipped by .inv-modal's overflow-y:auto (last field in a scrollable modal). */}
      <PortalDropdown
        open={calendarOpen}
        anchorRef={wrapperRef}
        onClose={() => setCalendarOpen(false)}
        placement="bottom-end"
        className="inv-date-picker-dropdown"
      >
        <InlineCalendar value={value} onChange={handleCalendarSelect} />
      </PortalDropdown>
    </div>
  )
}

export function RecordPaymentModal({
  invoiceId,
  invoiceNumber,
  amountDue,
  open,
  onClose,
  onRecorded,
}: RecordPaymentModalProps) {
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit_card')
  const [reference, setReference] = useState('')
  const [dateReceived, setDateReceived] = useState(new Date())
  const [notes, setNotes] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setAmount(formatWithCommas(amountDue.toFixed(2)))
      setPaymentMethod('credit_card')
      setReference('')
      setDateReceived(new Date())
      setNotes('')
      setError(null)
    }
  }, [open, invoiceId, amountDue])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await invoicesService.recordPayment(invoiceId, {
        amount: parseFloat(stripCommas(amount)) || 0,
        method: paymentMethod,
        reference: reference || undefined,
        notes: notes || undefined,
      })

      onRecorded()
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    onClose()
  }

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9.,]/g, '')
    setAmount(formatWithCommas(stripCommas(raw)))
  }

  return (
    <div className="inv-modal-overlay" onMouseDown={handleBackdropMouseDown} onKeyDown={handleKeyDown}>
      <div className="inv-modal">
        <div className="inv-modal-header">
          <h2>Record Payment</h2>
          <button
            onClick={handleClose}
            className="inv-modal-close"
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="inv-modal-body">
          {error && (
            <div className="inv-modal-error" role="alert">
              {error}
            </div>
          )}

          <div className="inv-modal-info">
            <p>
              <strong>Invoice:</strong> {invoiceNumber}
            </p>
            <p>
              <strong>Amount Due:</strong> ${amountDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          <div className="inv-modal-field">
            <label htmlFor="payment-amount" className="inv-modal-label">
              Amount
            </label>
            <div className="inv-modal-dollar-input">
              <span className="inv-modal-dollar-sign">$</span>
              <input
                id="payment-amount"
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                className="inv-modal-input inv-modal-input-dollar"
              />
            </div>
          </div>

          <div className="inv-modal-field">
            <label htmlFor="payment-method" className="inv-modal-label">
              Payment Method
            </label>
            <select
              id="payment-method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
              className="inv-modal-select"
            >
              <option value="credit_card">Credit Card</option>
              <option value="ach_bank_transfer">ACH/Bank Transfer</option>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="wire_transfer">Wire Transfer</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="inv-modal-field">
            <label htmlFor="reference" className="inv-modal-label">
              Reference / Check # (Optional)
            </label>
            <input
              id="reference"
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g., Check #12345 or transaction ID"
              className="inv-modal-input"
            />
          </div>

          <div className="inv-modal-field">
            <label className="inv-modal-label">
              Date Received
            </label>
            <DateReceivedPicker value={dateReceived} onChange={setDateReceived} />
          </div>

          <div className="inv-modal-field">
            <label htmlFor="payment-notes" className="inv-modal-label">
              Notes (Optional)
            </label>
            <textarea
              id="payment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this payment"
              className="inv-modal-textarea"
            />
          </div>
        </form>

        <div className="inv-modal-footer">
          <button
            type="button"
            onClick={handleClose}
            className="inv-modal-btn inv-modal-btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="inv-modal-btn inv-modal-btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
